import { COMPANY_NAME_NAME } from '@/features/ecommerce/components/InputCompanyName';
import { INVOICE_BUILDING_NUMBER_NAME } from '@/features/ecommerce/components/InputInvoiceBuildingNumber';
import { INVOICE_CITY_NAME } from '@/features/ecommerce/components/InputInvoiceCity';
import { INVOICE_POSTAL_CODE_NAME } from '@/features/ecommerce/components/InputInvoicePostalCode';
import { INVOICE_STREET_NAME } from '@/features/ecommerce/components/InputInvoiceStreet';
import { TAX_ID_NAME } from '@/features/ecommerce/components/InputTaxId';
import { IS_COMPANY_NAME } from '@/features/ecommerce/components/RadioGroupBuyerType';
import { PAYMENT_METHOD_NAME } from '@/features/ecommerce/components/RadioGroupPaymentMethod';
import { ECOMMERCE_STEP_NAME } from '@/features/ecommerce/const';
import {
  BILLING_FIELDS,
  DOCUMENT_FIELDS,
  type EcommerceFormValues,
} from '@/features/ecommerce/schema';
import Step3DocumentSelect from '@/features/ecommerce/steps/Step3DocumentSelect';
import Step4BillingData from '@/features/ecommerce/steps/Step4BillingData';
import { ExtendedSteps } from '@/features/v2/lib/wizard';

import { i18n } from '@lingui/core';

// `ExtendedSteps` has no slot for "which fields belong to this screen" — HH and
// VD gate their steps with `deadEnds` plus whole-form validity, while ecommerce
// validates with targeted `trigger()` calls. The mapping therefore lives here,
// next to the step it describes, instead of in the step components.
export interface EcommerceStep extends ExtendedSteps {
  fields: (innerStep?: number) => (keyof EcommerceFormValues)[];
}

// Index of the last inner screen of each step, i.e. what `lastStep()` reports.
const DOCUMENT_LAST_SCREEN = 1; // document → addons
const BILLING_LAST_SCREEN = 1; // billing data → summary

// `stepper.tsx` awards one point per truthy value and divides by the number of
// points, so `addons` can never be one — an empty array is JS-truthy and would
// always count. The document screen has a single required answer anyway.
export const documentProgressPoints = () => [...DOCUMENT_FIELDS];

export const billingProgressPoints = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: Record<any, any>
) => {
  // `payment_method` is deliberately absent — it lives on the summary screen,
  // which is reached with every billing point already filled (100%).
  const points: string[] = [...BILLING_FIELDS];

  const fieldsToRemove: string[] = [];

  // Invoice fields are only rendered once the purchase is for a company.
  if (value[IS_COMPANY_NAME] !== true) {
    fieldsToRemove.push(
      COMPANY_NAME_NAME,
      TAX_ID_NAME,
      INVOICE_STREET_NAME,
      INVOICE_BUILDING_NUMBER_NAME,
      INVOICE_POSTAL_CODE_NAME,
      INVOICE_CITY_NAME
    );
  }

  // `is_company: false` is a real answer, but it is falsy, so it would never
  // earn its point and the bar would stop short of 100%. Shrink the
  // denominator instead of leaving the numerator unreachable.
  if (value[IS_COMPANY_NAME] === false) {
    fieldsToRemove.push(IS_COMPANY_NAME);
  }

  return points.filter((field) => !fieldsToRemove.includes(field));
};

export const DOCUMENT_STEP: EcommerceStep = {
  label: i18n.t('Wybór dokumentu'),
  name: ECOMMERCE_STEP_NAME.DOCUMENT,
  component: Step3DocumentSelect,
  chatMessage: () => {
    return i18n.t(
      'Na podstawie Twoich odpowiedzi możemy przygotować dokument do Twojej sprawy. Wybierz wariant, który najlepiej pasuje do tego, jak dużo wsparcia potrzebujesz.'
    );
  },
  // Nothing precedes the document screen in this part of the funnel.
  backButton: (innerStep) => innerStep !== 0,
  lastStep: () => ({
    lastStep: DOCUMENT_LAST_SCREEN,
    additionalStep: false,
  }),
  fields: () => DOCUMENT_FIELDS,
  progressPoints: documentProgressPoints,
  // MoveForwardButton's debounce hook otherwise disables Next based on
  // whole-form formState.isValid — meaningless here since FormComponent
  // mounts every step's fields at once and each step validates its own
  // fields via targeted trigger() calls, not the form as a whole.
  completeValidation: () => true,
};

export const BILLING_STEP: EcommerceStep = {
  label: i18n.t('Dane do zamówienia'),
  name: ECOMMERCE_STEP_NAME.BILLING,
  component: Step4BillingData,
  chatMessage: (_userName: string, innerStep?: number) => {
    return innerStep === BILLING_LAST_SCREEN
      ? i18n.t(
          'Sprawdź zamówienie. Jeśli wszystko się zgadza, przejdź do bezpiecznej płatności.'
        )
      : i18n.t(
          'Uzupełnij dane do zamówienia. Użyjemy ich do potwierdzenia zakupu, przygotowania dokumentu i — jeśli wybierzesz fakturę — do jej wystawienia.'
        );
  },
  backButton: () => true,
  lastStep: () => ({
    lastStep: BILLING_LAST_SCREEN,
    additionalStep: false,
  }),
  // The summary screen adds the payment method to what the billing screen
  // already requires.
  fields: (innerStep) =>
    innerStep === BILLING_LAST_SCREEN
      ? [...BILLING_FIELDS, PAYMENT_METHOD_NAME]
      : BILLING_FIELDS,
  progressPoints: billingProgressPoints,
  completeValidation: () => true,
};

// Each step component reads its own entry directly (`DOCUMENT_STEP` /
// `BILLING_STEP`) instead of `EcommerceSchema[step]` as HH does: FormComponent
// keeps every step mounted, so indexing by the active step would make a hidden
// step render the neighbour's buttons.
export const EcommerceSchema: EcommerceStep[] = [DOCUMENT_STEP, BILLING_STEP];
